import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { Bell, Eye, History, Loader2, RefreshCw, StickyNote } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";
import { useAuth } from "../../context/AuthContext";
import { canAccessFinanceModule } from "../../utils/roleAccess";
import FinanceAccessDenied from "../../components/finance/FinanceAccessDenied";
import FinanceApiErrorBanner from "../../components/finance/FinanceApiErrorBanner";
import EncaissementFactureModal, {
  type FactureEncaissementContext,
} from "../../components/finance/EncaissementFactureModal";
import {
  AdminDataTableShell,
  AdminEmptyState,
  AdminKpiCard,
  AdminPageHero,
  adminHeroActionClassName,
} from "../../modules/admin/ui";

type Row = {
  id: number;
  numero: string;
  dateEmission: string;
  dateEcheance: string | null;
  montantTTC: number;
  montantPaye: number;
  resteAPayer: number;
  devise: string;
  statut: string;
  retardJours: number;
  client: {
    id: number;
    nom?: string | null;
    prenom?: string | null;
    nomEntreprise?: string | null;
    email?: string | null;
    telephone?: string | null;
    scorePaiement?: number | null;
    niveauRisque?: string | null;
    totalImpayes?: number;
    retardMoyen?: number;
  } | null;
  derniereRelance: string | null;
  prochaineRelance: string | null;
  lastEncaissementAt: string | null;
};

type ApiResp = {
  success?: boolean;
  items?: Row[];
  kpis?: {
    totalARecouvrer: number;
    facturesImpayees: number;
    facturesEnRetard: number;
    clientsDebiteurs: number;
    paiementsDuMois: { count: number; montant: number };
    clientsCritiques?: number;
    totalImpayeKpi?: number;
    tauxRecouvrement?: number;
    delaiMoyenPaiementJours?: number;
  };
  pagination?: { page: number; limit: number; total: number };
};

function risqueBadgeClass(niveau: string | null | undefined): string {
  const n = String(niveau || "").toUpperCase();
  if (n === "CRITIQUE") return "border-rose-600 bg-rose-950/90 text-rose-100";
  if (n === "ELEVE") return "border-orange-600 bg-orange-950/90 text-orange-100";
  if (n === "MOYEN") return "border-amber-600 bg-amber-950/90 text-amber-100";
  if (n === "FAIBLE") return "border-emerald-600 bg-emerald-950/90 text-emerald-100";
  return "border-slate-600 bg-slate-800 text-slate-300";
}

function clientLabel(c: Row["client"], fallbackId: number): string {
  if (!c) return `Client #${fallbackId}`;
  return (
    (c.nomEntreprise?.trim() ||
      [c.prenom, c.nom].filter(Boolean).join(" ").trim() ||
      `Client #${c.id}`) as string
  );
}

function statutUi(statut: string): string {
  const s = String(statut).toUpperCase();
  if (s === "IMPAYEE") return "Impayée";
  if (s === "PARTIELLEMENT_PAYEE") return "Partielle";
  if (s === "PAYEE") return "Payée";
  if (s === "ANNULEE") return "Annulée";
  if (s === "BROUILLON") return "Brouillon";
  return statut;
}

const LITIGE_KEY = "finance_litige_facture_ids";

function readLitiges(): Set<number> {
  try {
    const raw = localStorage.getItem(LITIGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as number[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

export default function RecouvrementPage(): React.JSX.Element {
  const { user, isLoading: authLoading } = useAuth();

  const [rows, setRows] = useState<Row[]>([]);
  const [kpis, setKpis] = useState<ApiResp["kpis"]>();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statut, setStatut] = useState("");
  const [retard30, setRetard30] = useState(false);
  const [niveauRisque, setNiveauRisque] = useState("");
  const [scoreMin, setScoreMin] = useState("");
  const [scoreMax, setScoreMax] = useState("");
  const [retardMinJours, setRetardMinJours] = useState("");
  const [modalFacture, setModalFacture] = useState<FactureEncaissementContext | null>(null);
  const [litiges, setLitiges] = useState<Set<number>>(() => readLitiges());

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const load = useCallback(async () => {
    setForbidden(false);
    setServerError(null);
    setLoading(true);
    try {
      const rMin = retardMinJours.trim() !== "" ? Number(retardMinJours) : NaN;
      const hasRetardMin = Number.isFinite(rMin) && rMin > 0;
      const smin = scoreMin.trim() !== "" ? Number(scoreMin) : undefined;
      const smax = scoreMax.trim() !== "" ? Number(scoreMax) : undefined;
      const { data } = await api.get<ApiResp>("/finance/recouvrement", {
        params: {
          page,
          limit: 50,
          search: debouncedSearch || undefined,
          statut: statut && statut !== "LITIGE" ? statut : undefined,
          retardGt30: hasRetardMin ? undefined : retard30 ? true : undefined,
          retardMinDays: hasRetardMin ? rMin : undefined,
          niveauRisque: niveauRisque || undefined,
          scoreMin: smin != null && Number.isFinite(smin) ? smin : undefined,
          scoreMax: smax != null && Number.isFinite(smax) ? smax : undefined,
        },
      });
      setRows(Array.isArray(data?.items) ? data.items : []);
      setKpis(data?.kpis);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setForbidden(true);
        setRows([]);
        return;
      }
      if (isAxiosError(e) && e.response?.status === 500) {
        const msg =
          (e.response?.data as { message?: string })?.message ||
          "Impossible de charger le recouvrement.";
        setServerError(msg);
        setRows([]);
        return;
      }
      console.error(e);
      toast.error("Erreur lors du chargement du recouvrement.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statut, retard30, niveauRisque, scoreMin, scoreMax, retardMinJours]);

  useEffect(() => {
    void load();
  }, [load]);

  const displayedRows = useMemo(() => {
    return rows.filter((r) => {
      if (statut === "LITIGE") return litiges.has(r.id);
      return true;
    });
  }, [rows, statut, litiges]);

  const openEncaissement = (r: Row) => {
    const cl = clientLabel(r.client, r.client?.id ?? r.id);
    setModalFacture({
      id: r.id,
      numero: r.numero,
      clientLabel: cl,
      montantTTC: r.montantTTC,
      montantPaye: r.montantPaye,
      resteAPayer: r.resteAPayer,
      devise: r.devise || "CDF",
      clientId: r.client?.id ?? 0,
    });
  };

  const toggleLitige = (id: number) => {
    const next = new Set(litiges);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setLitiges(next);
    localStorage.setItem(LITIGE_KEY, JSON.stringify([...next]));
    toast.success(next.has(id) ? "Marqué en litige (local)." : "Litige retiré.");
  };

  const relancer = (r: Row) => {
    const email = r.client?.email;
    const tel = r.client?.telephone;
    if (email) {
      window.location.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(
        `Relance facture ${r.numero}`
      )}&body=${encodeURIComponent(`Bonjour,\n\nLa facture ${r.numero} présente un solde de ${r.resteAPayer.toLocaleString("fr-FR")}.\n\nCordialement,\n`)})}`;
      return;
    }
    if (tel) {
      toast.info(`Contacter le client au ${tel}.`);
      return;
    }
    toast.info("Coordonnées insuffisantes pour une relance.");
  };

  if (authLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
      </div>
    );
  }

  if (!user || !canAccessFinanceModule(user.role)) {
    return <FinanceAccessDenied />;
  }

  if (forbidden) {
    return <FinanceAccessDenied title="Accès refusé" detail="Lecture du recouvrement refusée (403)." />;
  }

  return (
    <div className="flex w-full min-w-0 max-w-none flex-col gap-6">
      <AdminPageHero
        eyebrow="Finance"
        title="Recouvrement"
        subtitle="Factures impayées ou partiellement payées — tout encaissement est lié à une facture."
        actions={
          <>
            <button
              type="button"
              onClick={() => void load()}
              className={adminHeroActionClassName(false)}
            >
              <RefreshCw className={`mr-2 inline h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <Link to="/admin/finance/paiements" className={adminHeroActionClassName(false)}>
              Paiements
            </Link>
            <Link to="/admin/finance/factures" className={adminHeroActionClassName(false)}>
              Factures
            </Link>
          </>
        }
      />

      {serverError ? <FinanceApiErrorBanner message={serverError} onRetry={() => void load()} /> : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <AdminKpiCard
          label="Total à recouvrer"
          value={(kpis?.totalARecouvrer ?? 0).toLocaleString("fr-FR")}
        />
        <AdminKpiCard label="Factures impayées" value={String(kpis?.facturesImpayees ?? "—")} />
        <AdminKpiCard label="Factures en retard" value={String(kpis?.facturesEnRetard ?? "—")} />
        <AdminKpiCard label="Clients débiteurs" value={String(kpis?.clientsDebiteurs ?? "—")} />
        <AdminKpiCard
          label="Paiements du mois"
          value={`${kpis?.paiementsDuMois?.count ?? 0} · ${(kpis?.paiementsDuMois?.montant ?? 0).toLocaleString("fr-FR")}`}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <AdminKpiCard
          label="Clients critiques"
          value={String(kpis?.clientsCritiques ?? "—")}
        />
        <AdminKpiCard
          label="Total impayé (KPI)"
          value={(kpis?.totalImpayeKpi ?? kpis?.totalARecouvrer ?? 0).toLocaleString("fr-FR")}
        />
        <AdminKpiCard
          label="Taux de recouvrement"
          value={`${kpis?.tauxRecouvrement ?? 0} %`}
        />
        <AdminKpiCard
          label="Délai moyen de paiement"
          value={`${kpis?.delaiMoyenPaiementJours ?? 0} j`}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setPage(1);
              void load();
            }
          }}
          placeholder="Client, n° facture… (Entrée)"
          className="min-w-[200px] flex-1 rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-sm text-white placeholder:text-slate-500"
        />
        <select
          value={statut}
          onChange={(e) => {
            setStatut(e.target.value);
            setPage(1);
          }}
          className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
        >
          <option value="">Tous statuts</option>
          <option value="IMPAYEE">Impayée</option>
          <option value="PARTIELLE">Partielle</option>
          <option value="EN_RETARD">En retard (échue)</option>
          <option value="LITIGE">Litige (marqué)</option>
        </select>
        <select
          value={niveauRisque}
          onChange={(e) => {
            setNiveauRisque(e.target.value);
            setPage(1);
          }}
          className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
        >
          <option value="">Tous risques</option>
          <option value="CRITIQUE">Critique</option>
          <option value="ELEVE">Élevé</option>
          <option value="MOYEN">Moyen</option>
          <option value="FAIBLE">Faible</option>
        </select>
        <input
          type="number"
          min={0}
          max={100}
          placeholder="Score min"
          value={scoreMin}
          onChange={(e) => {
            setScoreMin(e.target.value);
            setPage(1);
          }}
          className="w-[100px] rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500"
        />
        <input
          type="number"
          min={0}
          max={100}
          placeholder="Score max"
          value={scoreMax}
          onChange={(e) => {
            setScoreMax(e.target.value);
            setPage(1);
          }}
          className="w-[100px] rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500"
        />
        <input
          type="number"
          min={1}
          placeholder="Retard min (j)"
          value={retardMinJours}
          onChange={(e) => {
            setRetardMinJours(e.target.value);
            setPage(1);
          }}
          className="min-w-[120px] rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500"
        />
        <label className="flex items-center gap-2 text-sm text-slate-400">
          <input
            type="checkbox"
            checked={retard30}
            onChange={(e) => {
              setRetard30(e.target.checked);
              setPage(1);
            }}
          />
          Retard &gt; 30 j.
        </label>
      </div>

      <AdminDataTableShell>
        {loading ? (
          <div className="flex justify-center py-16 text-slate-400">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : displayedRows.length === 0 ? (
          <AdminEmptyState
            title="Aucune facture à recouvrer"
            description="Les soldes réglés ou les filtres actuels masquent les lignes."
            action={
              <Link
                to="/admin/finance/factures"
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500"
              >
                Facturation
              </Link>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-slate-200">
              <thead className="border-b border-slate-800 bg-slate-950/80 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3">Client</th>
                  <th className="px-3 py-3 text-center">Score</th>
                  <th className="px-3 py-3">Risque</th>
                  <th className="px-3 py-3">Facture</th>
                  <th className="px-3 py-3">Émission</th>
                  <th className="px-3 py-3">Échéance</th>
                  <th className="px-3 py-3 text-right">TTC</th>
                  <th className="px-3 py-3 text-right">Payé</th>
                  <th className="px-3 py-3 text-right">Reste</th>
                  <th className="px-3 py-3">Retard</th>
                  <th className="px-3 py-3">Statut</th>
                  <th className="px-3 py-3">Dernière relance</th>
                  <th className="px-3 py-3">Prochaine relance</th>
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((r) => {
                  const jr = r.retardJours;
                  const jrCls =
                    jr === 0 ? "text-slate-400" : jr <= 7 ? "text-amber-300" : "text-rose-400 font-semibold";
                  return (
                    <tr key={r.id} className="border-b border-slate-800/80 hover:bg-slate-800/40">
                      <td className="px-3 py-3">{clientLabel(r.client, r.id)}</td>
                      <td className="px-3 py-3 text-center tabular-nums text-slate-200">
                        {r.client?.scorePaiement != null ? r.client.scorePaiement : "—"}
                      </td>
                      <td className="px-3 py-3">
                        {r.client?.niveauRisque ? (
                          <span
                            className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${risqueBadgeClass(
                              r.client.niveauRisque
                            )}`}
                          >
                            {r.client.niveauRisque}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-white">{r.numero}</td>
                      <td className="px-3 py-3 text-slate-400">
                        {dayjs(r.dateEmission).format("DD/MM/YYYY")}
                      </td>
                      <td className="px-3 py-3 text-slate-400">
                        {r.dateEcheance ? dayjs(r.dateEcheance).format("DD/MM/YYYY") : "—"}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        {r.montantTTC.toLocaleString("fr-FR")}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-300">
                        {r.montantPaye.toLocaleString("fr-FR")}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-amber-100">
                        {r.resteAPayer.toLocaleString("fr-FR")}
                      </td>
                      <td className={`px-3 py-3 tabular-nums ${jrCls}`}>{jr} j</td>
                      <td className="px-3 py-3 text-xs">{statutUi(r.statut)}</td>
                      <td className="px-3 py-3 text-xs text-slate-400">
                        {r.derniereRelance
                          ? dayjs(r.derniereRelance).format("DD/MM/YYYY HH:mm")
                          : "—"}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-400">
                        {r.prochaineRelance ? dayjs(r.prochaineRelance).format("DD/MM/YYYY") : "—"}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEncaissement(r)}
                            className="rounded-lg bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-500"
                          >
                            Encaisser
                          </button>
                          <button
                            type="button"
                            onClick={() => relancer(r)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1 text-xs text-white hover:bg-slate-800"
                          >
                            <Bell className="h-3 w-3" />
                            Relancer
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleLitige(r.id)}
                            className="rounded-lg border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                          >
                            {litiges.has(r.id) ? "Litige ✓" : "Litige"}
                          </button>
                          <Link
                            to={`/admin/finance/factures/${r.id}`}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1 text-xs text-white hover:bg-slate-800"
                          >
                            <Eye className="h-3 w-3" />
                            Facture
                          </Link>
                          <Link
                            to={`/admin/finance/factures/${r.id}`}
                            state={{ openHistoriquePaiements: true }}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1 text-xs text-sky-300 hover:bg-slate-800"
                          >
                            <History className="h-3 w-3" />
                            Paiements
                          </Link>
                          <button
                            type="button"
                            onClick={() => {
                              localStorage.setItem(`recouvrement_suivi_${r.id}`, new Date().toISOString());
                              toast.success("Suivi noté.");
                            }}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1 text-xs text-white hover:bg-slate-800"
                          >
                            <StickyNote className="h-3 w-3" />
                            Suivi
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminDataTableShell>

      <EncaissementFactureModal
        open={modalFacture != null}
        facture={modalFacture}
        onClose={() => setModalFacture(null)}
        onSuccess={() => void load()}
      />
    </div>
  );
}
